import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
} from '@monksflow/monks-ui';
import { ListTree } from 'lucide-react';
import type { TaxonomyField } from '@/entities/taxonomy';
import {
  taxonomyListDetailQueryOptions,
  taxonomyListsQueryOptions,
} from '@/entities/taxonomy-list';
import {
  defaultCodeColumnId,
  resolveBoundColumnPair,
  resolveBoundListVersion,
} from '@inference.engine/taxonomy/web';
import { FormSheet } from '@/shared/ui/FormSheet';
import { isBlankValue } from '../../lib/pruneEmptyRows';
import { isBindableList } from '../ListBinding/isBindableList';
import { SAME_AS_DISPLAY } from '../ListBinding/sentinels';
import { bindableColumns } from '@/entities/taxonomy-list';

export interface LinkListModalProps {
  open: boolean;
  field: TaxonomyField;
  onClose: () => void;
  onSave: (listRef: NonNullable<TaxonomyField['listRef']>) => void;
}

/**
 * Binds a schema column to a shared list.
 *
 * The two column pickers are the crux: a list is a TABLE, so the user must say
 * which column supplies the display value and which supplies the output code —
 * that can't be inferred. "Follow the list" (no pinned version) is the default
 * because auto-propagation is the reason shared lists exist; pinning is offered
 * for schemas that must freeze their vocabulary.
 */
export function LinkListModal({ open, field, onClose, onSave }: LinkListModalProps) {
  const { t } = useTranslation('knowledge');
  const existing = field.listRef;
  const [listId, setListId] = useState<string>(existing?.listId ?? '');
  const [valueColumnId, setValueColumnId] = useState<string>(existing?.valueColumnId ?? '');
  /** `null` is "same as display value"; `undefined` means nothing chosen yet, so the default applies. */
  const [codeColumnId, setCodeColumnId] = useState<string | null | undefined>(
    existing ? (existing.codeColumnId ?? null) : undefined,
  );
  const [follow, setFollow] = useState<boolean>(!existing?.listVersionId);

  useEffect(() => {
    if (!open) return;
    setListId(existing?.listId ?? '');
    setValueColumnId(existing?.valueColumnId ?? '');
    setCodeColumnId(existing ? (existing.codeColumnId ?? null) : undefined);
    setFollow(!existing?.listVersionId);
  }, [open, existing]);

  const listsQuery = useQuery(taxonomyListsQueryOptions());
  const lists = listsQuery.data ?? [];
  const detailQuery = useQuery({ ...taxonomyListDetailQueryOptions(listId), enabled: !!listId });

  /**
   * The version this binding will READ, by the same rule the generator uses —
   * the latest published one, not the list's head.
   *
   * The columns matter as much as the values: offering the HEAD's columns let
   * someone bind a code column that exists only on an unpublished draft, and the
   * binding then resolved to nothing at all. Pinning had the same shape, freezing
   * a schema onto a draft that stays editable.
   */
  const target = useMemo(
    // The EXISTING pin, not a fresh resolve. Passing `{listVersionId: null}`
    // answered "what would a new binding read", so re-opening a pinned column
    // and pressing Save silently moved the pin to the latest published version —
    // and the explainer below announced that version as the one it was frozen
    // to, which it was not. Only a list the user just picked has no pin to keep.
    () =>
      resolveBoundListVersion(detailQuery.data, {
        listVersionId: listId === existing?.listId ? existing.listVersionId : null,
      }),
    [detailQuery.data, listId, existing],
  );

  const columns = target?.version.columns ?? [];
  const pickableColumns = bindableColumns(
    columns,
    listId === existing?.listId ? [existing.valueColumnId, existing.codeColumnId] : [],
  );

  // Pre-select sensible columns once a list is chosen, using the same rule the
  // automatic binding paths use rather than a second copy of it — this dialog is
  // where a person confirms the guess, so the two must not disagree about what
  // the guess is. Both stay editable.
  // A deliberate "same as display value" (`null`) is a choice, so only an unchosen or
  // vanished code column is replaced.
  useEffect(() => {
    if (!columns.length) return;
    const pair = resolveBoundColumnPair(columns);
    if (!columns.some((c) => c.id === valueColumnId)) {
      setValueColumnId((pair?.valueColumn ?? columns[0]).id);
    }
    const vanished = !!codeColumnId && !columns.some((c) => c.id === codeColumnId);
    if (codeColumnId === undefined || vanished) {
      setCodeColumnId(defaultCodeColumnId(columns, target?.version.rows) ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const selectList = (next: string) => {
    setListId(next);
    setValueColumnId(next === existing?.listId ? existing.valueColumnId : '');
    setCodeColumnId(next === existing?.listId ? (existing.codeColumnId ?? null) : undefined);
    setFollow(next === existing?.listId ? !existing.listVersionId : true);
  };

  const rowCount = target?.version.rows.length ?? 0;
  // The grid keeps a blank row for typing, and blank rows between typed ones, in `values`.
  const typedCount = field.values.filter((v) => !isBlankValue(v)).length;
  const canSave = !!listId && !!valueColumnId && !detailQuery.isPending;
  const keepsExistingPin = listId === existing?.listId && !!existing.listVersionId;
  // A list that never published has only an editable draft, and publish refuses a pin to a draft.
  const canPin = keepsExistingPin || target?.source === 'published' || target?.source === 'pinned';

  const handleSave = () => {
    if (!canSave) return;
    // A pin that no longer resolves stays as stored rather than turning into a follow.
    const keptPin = listId === existing?.listId ? (existing.listVersionId ?? null) : null;
    const listVersionId = follow || !canPin ? null : (target?.version.id ?? keptPin);
    const pinUnchanged =
      listId === existing?.listId && (existing.listVersionId ?? null) === listVersionId;
    onSave({
      listId,
      listVersionId,
      // Unpublishing releases only pins that publishing made, so the marker must survive a
      // save that left the pin alone.
      ...(pinUnchanged && existing.pinnedBy ? { pinnedBy: existing.pinnedBy } : {}),
      valueColumnId,
      codeColumnId: codeColumnId ?? null,
    });
    onClose();
  };

  return (
    <FormSheet
      className="w-[34rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={
        existing
          ? t('taxonomy.linkListModal.editListBindingFor', { name: field.name })
          : t('taxonomy.linkListModal.useSharedListFor', { name: field.name })
      }
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
          <ListTree className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {t('taxonomy.linkListModal.columnAposSOptionsWill')}
        </p>

        <div className="flex flex-col gap-1.5">
          <Label>
            {t('taxonomy.linkListModal.list')} <span className="text-destructive">*</span>
          </Label>
          <Select value={listId} onValueChange={selectList}>
            <SelectTrigger>
              <SelectValue placeholder={t('taxonomy.linkListModal.selectList')} />
            </SelectTrigger>
            <SelectContent>
              {lists.length === 0 ? (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  {t('taxonomy.linkListModal.noListsYetCreateOne')}
                </div>
              ) : (
                lists.map((l) =>
                  isBindableList(l, existing?.listId) ? (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} ({t('taxonomy.linkListModal.optionCount', { count: l.rowCount })})
                    </SelectItem>
                  ) : (
                    <SelectItem key={l.id} value={l.id} disabled>
                      {t('taxonomy.listBinding.listNeedsTwoColumns', { name: l.name })}
                    </SelectItem>
                  ),
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {listId && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex min-w-44 flex-1 flex-col gap-1.5">
              <Label>
                {t('taxonomy.linkListModal.displayValueColumn')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Select value={valueColumnId} onValueChange={setValueColumnId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('taxonomy.linkListModal.selectColumn')} />
                </SelectTrigger>
                <SelectContent>
                  {pickableColumns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex min-w-44 flex-1 flex-col gap-1.5">
              <Label>{t('taxonomy.linkListModal.outputCodeColumn')}</Label>
              <Select
                value={codeColumnId ?? SAME_AS_DISPLAY}
                onValueChange={(v) => setCodeColumnId(v === SAME_AS_DISPLAY ? null : v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SAME_AS_DISPLAY}>
                    {t('taxonomy.linkListModal.sameDisplayValue')}
                  </SelectItem>
                  {pickableColumns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {listId && (
          <div className="flex items-center justify-between border-t border-border-light pt-4">
            <div className="flex min-w-0 flex-col pr-3">
              <span className="text-sm font-medium">{t('taxonomy.linkListModal.followList')}</span>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.linkListModal.followOrPinExplainer', {
                  version: target?.version.versionNumber ?? '—',
                })}
              </span>
            </div>
            <Switch
              checked={follow}
              onCheckedChange={setFollow}
              disabled={follow && !canPin}
              aria-label={t('taxonomy.linkListModal.followListHead')}
            />
          </div>
        )}

        {listId && !existing && typedCount > 0 && (
          <p className="text-xs text-muted-foreground">
            {t('taxonomy.linkListModal.optionsWillReplace', {
              count: rowCount,
              typed: typedCount,
            })}
          </p>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label: existing ? t('taxonomy.linkListModal.save') : t('taxonomy.linkListModal.useList'),
          onClick: handleSave,
          disabled: !canSave,
        }}
        secondary={{ label: t('taxonomy.linkListModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

LinkListModal.displayName = 'LinkListModal';
